import {
  select,
  event,
  zoomTransform,
  format,
  timeFormat,
  line as lineD3,
  area as areaD3,
  axisLeft,
} from "d3";
import {
  colorDarkExpo,
  grayLightBackground,
  textactive2,
} from "@/_core/_helpers/_parametric_tools";
import {
  dotCreate,
  hLineCreate,
  vLineCreate,
} from "@/_core/charts/D3ChartHoverHelpers";
import {
  create2YTooltips,
  inRange,
  mouseDate,
  roundDate,
  zoomSetup,
} from "@/_core/charts/TechnicalAnalysis";
import { populateTableComparison } from "@/_core/data/Markets";
import { additionalYSpace } from "@/_core/_helpers/_helpers";

export function ComparisonSpreadHover(
  _this,
  isTwoAxes,
  isTwoSeries,
  colorTheme,
) {
  const auxName = "Spread";
  let valueLast = _this.chartDataRaw[_this.chartDataRaw.length - 1].macd;
  let xTooltipWidth = 80;
  let yTooltip = create2YTooltips(
    _this.svgNodeSpread,
    _this.currencyFormat,
    valueLast,
    xTooltipWidth,
    colorDarkExpo(colorTheme),
    colorDarkExpo(colorTheme),
  );
  let y2Tooltip = yTooltip["y2Tooltip"];
  let xTooltip = yTooltip["xTooltip"];
  yTooltip = yTooltip["yTooltip"];
  hLineCreate(_this.svgNodeSpread, auxName, grayLightBackground(colorTheme), 1);
  vLineCreate(_this.svgNodeSpread, auxName, grayLightBackground(colorTheme), 1);
  dotCreate(
    _this.svgNodeSpread,
    auxName,
    3,
    textactive2(colorTheme),
    colorDarkExpo(colorTheme),
    3,
  );

  function mouseover() {
    if (_this.transform === undefined || _this.transform == null) {
      _this.transform = zoomTransform(
        select(".hoverParent" + auxName)._groups[0][0],
      );
    }
    event.preventDefault();
    const xPixels = Math.max(
      Math.min(
        event.layerX - _this.svgNodeSpread.margin.left,
        _this.svgNodeSpread.width,
      ),
      0,
    );
    var xt = _this.transform.rescaleX(_this.svgNodeSpread.x);
    var yt = _this.svgNodeSpread.y;
    let dat = roundDate(xt.invert(xPixels));
    let selData;

    let highlightCandles = true;
    if (dat <= _this.chartDataRaw[_this.chartDataRaw.length - 1].date) {
      selData = mouseDate(xt.invert(xPixels), _this.chartDataRaw);
    } else {
      selData = JSON.parse(
        JSON.stringify(_this.chartDataRaw[_this.chartDataRaw.length - 1]),
      );
      selData["date"] = dat;
      highlightCandles = false;
    }
    const yPixels = Math.max(
      Math.min(
        event.layerY - _this.svgNodeSpread.margin.top,
        _this.svgNodeSpread.height,
      ),
      0,
    );
    let yRate = yt.invert(yPixels);
    yTooltip
      .style("opacity", 1)
      .style(
        "right",
        _this.svgNodeSpread.width + _this.svgNodeSpread.margin.right + 2 + "px",
      )
      .style("top", yPixels + _this.svgNodeSpread.margin.top - 11 + "px")
      .html(format(_this.currencyFormat)(yRate));
    y2Tooltip
      .style("opacity", 1)
      .style(
        "left",
        _this.svgNodeSpread.width + _this.svgNodeSpread.margin.left + 2 + "px",
      )
      .style("top", yPixels + _this.svgNodeSpread.margin.top - 11 + "px")
      .html(format(_this.currencyFormat)(yRate));
    xTooltip
      .style("opacity", 1)
      .style(
        "left",
        xt(selData.date) +
          _this.svgNodeSpread.margin.left -
          xTooltipWidth / 2 +
          "px",
      )
      .style(
        "top",
        _this.svgNodeSpread.height + _this.svgNodeSpread.margin.top + 2 + "px",
      )
      .html(timeFormat(_this.dateFormat)(selData.date));

    select(".hHoverLineSpread")
      .style("opacity", 1)
      .attr("y1", yPixels)
      .attr("y2", yPixels);

    let xPix = xt(selData.date);

    select(".vHoverLineTA")
      .style("opacity", 1)
      .attr("x1", xPix)
      .attr("x2", xPix);
    select(".vHoverLineSpread")
      .style("opacity", 1)
      .attr("x1", xPix)
      .attr("x2", xPix);
    select(".vHoverLineCorrel")
      .style("opacity", 1)
      .attr("x1", xPix)
      .attr("x2", xPix);

    if (highlightCandles) {
      const yt2 = _this.svgNode.y;
      select(".dotTA")
        .style("opacity", 1)
        .attr(
          "transform",
          `translate(${xt(selData.date)},${yt2(selData[_this.fieldUp])})`,
        );
      if (isTwoSeries) {
        const yt22 = isTwoAxes ? _this.svgNode.y2 : _this.svgNode.y;
        select(".dotTA2")
          .style("opacity", 1)
          .attr(
            "transform",
            `translate(${xt(selData.date)},${yt22(selData[_this.fieldUp2])})`,
          );
      }

      select(".dotSpread")
        .style("opacity", 1)
        .attr(
          "transform",
          `translate(${xt(selData.date)},${yt(
            selData[_this.spreadFieldName],
          )})`,
        );
      const yt4 = _this.svgNodeCorrel.y;
      select(".dotCorrel")
        .style("opacity", 1)
        .attr(
          "transform",
          `translate(${xt(selData.date)},${yt4(selData["corr"])})`,
        );
    } else {
      select(".dotTA").style("opacity", 0);
      if (isTwoSeries) {
        select(".dotTA2").style("opacity", 0);
      }
      select(".dotSpread").style("opacity", 0);
      select(".dotCorrel").style("opacity", 0);
    }

    populateTableComparison(
      {
        date: selData.date,
        main: selData[_this.fieldUp],
        second: selData[_this.fieldUp2],
        spread: selData[_this.spreadFieldName],
        corr: selData.corr,
      },
      format(_this.currencyFormat),
      timeFormat("%d %b %y"),
    );
  }
  function mouseout() {
    yTooltip
      .style("right", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    y2Tooltip
      .style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    xTooltip
      .style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    select(".vHoverLineTA").style("opacity", 0);
    select(".hHoverLineSpread").style("opacity", 0);
    select(".vHoverLineSpread").style("opacity", 0);
    select(".vHoverLineCorrel").style("opacity", 0);
    select(".dotTA").style("opacity", 0);
    if (isTwoSeries) {
      select(".dotTA2").style("opacity", 0);
    }
    select(".dotSpread").style("opacity", 0);
    select(".dotCorrel").style("opacity", 0);

    let d = _this.chartDataRaw[_this.chartDataRaw.length - 1];
    populateTableComparison(
      {
        date: d.date,
        main: d[_this.fieldUp],
        second: d[_this.fieldUp2],
        spread: d[_this.spreadFieldName],
        corr: d.corr,
      },
      format(_this.currencyFormat),
      timeFormat("%d %b %y"),
    );
  }
  function zoomed() {
    if (!_this.keepDateInterval) {
      setTimeout(() => {
        _this.pickedDateInterval = "-1";
      }, 500);
    }
    yTooltip
      .style("right", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    y2Tooltip
      .style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    xTooltip
      .style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");

    select(".vHoverLineTA").style("opacity", 0);

    select(".hHoverLineSpread").style("opacity", 0);
    select(".vHoverLineSpread").style("opacity", 0);
    select(".vHoverLineCorrel").style("opacity", 0);
    select(".dotTA").style("opacity", 0);
    if (isTwoSeries) {
      select(".dotTA2").style("opacity", 0);
    }
    select(".dotSpread").style("opacity", 0);
    select(".dotCorrel").style("opacity", 0);

    _this.transform = event.transform;
    _this.svgNodeSpread.gX.call(
      _this.svgNodeSpread.xAxis.scale(
        _this.transform.rescaleX(_this.svgNodeSpread.x),
      ),
    );
    const xt = _this.transform.rescaleX(_this.svgNodeSpread.x);
    SpreadZooming(_this.transform, xt, _this);

    // transform other charts
    if (_this.externalZoom === false) {
      _this.externalZoom = true;
      select(".hoverParentTA").call(_this.zoomTA.transform, _this.transform);
      select(".hoverParentCorrel").call(
        _this.zoomCorrel.transform,
        _this.transform,
      );
      _this.externalZoom = false;
    }
  }
  function zoomEnd() {
    _this.transform = event.transform;
    const xt = _this.transform.rescaleX(_this.svgNodeSpread.x);
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function() {
      var xmin = new Date(xt.invert(0));
      var xmax = new Date(xt.invert(_this.svgNodeSpread.width));

      if (!_this.externalZoom) {
        let data = _this.chartDataRaw.map((d) => d);
        _this.filteredData = data.filter(function(d) {
          return inRange(d.date, xmin, xmax);
        });
      }

      let minMaxYAxis = _this.processData.minMax(
        _this.filteredData,
        _this.spreadFieldName,
        _this.spreadFieldName,
      );
      minMaxYAxis = additionalYSpace(minMaxYAxis, 0.1);
      if (!isFinite(minMaxYAxis[0])) {
        minMaxYAxis[0] = -1;
      }
      if (!isFinite(minMaxYAxis[1])) {
        minMaxYAxis[1] = 1;
      }
      _this.svgNodeSpread.y.domain(minMaxYAxis);
      let yt = _this.svgNodeSpread.y;

      SpreadZoomEnd(_this.transform, xt, yt, _this.filteredData, _this);
    }, 200);
  }

  var resizeTimer;
  zoomSetup(
    _this,
    "svgNodeSpread",
    "zoom" + auxName,
    auxName,
    "",
    mouseover,
    mouseout,
    zoomed,
    zoomEnd,
  );
}

export function SpreadZooming(t, xt, _this) {
  let area, line;
  line = lineD3()
    .defined((d) => !Number.isNaN(d[_this.spreadFieldName]))
    .x((d) => xt(d.date))
    .y((d) => _this.svgNodeSpread.y(d[_this.spreadFieldName]));
  _this.svgNodeSpread["upperLineSpread"].attr("d", (d) => line(d));
  line = lineD3()
    .defined((d) => !Number.isNaN(d["zero"]))
    .x((d) => xt(d.date))
    .y((d) => _this.svgNodeSpread.y(d["zero"]));
  _this.svgNodeSpread["lowerLineSpread"].attr("d", (d) => line(d));
  area = areaD3()
    .defined((d) => !Number.isNaN(d[_this.spreadFieldName]))
    .defined((d) => !Number.isNaN(d["zero"]))
    .x((d) => xt(d.date))
    .y0((d) => _this.svgNodeSpread.y(d[_this.spreadFieldName]))
    .y1((d) => _this.svgNodeSpread.y(d["zero"]));
  _this.svgNodeSpread["areaSpread"].attr("d", area);
}

export function SpreadZoomEnd(t, xt, yt, filtered, _this) {
  let area, line;
  line = lineD3()
    .defined((d) => !Number.isNaN(d[_this.spreadFieldName]))
    .x((d) => xt(d.date))
    .y((d) => yt(d[_this.spreadFieldName]));
  _this.svgNodeSpread["upperLineSpread"]
    .data([filtered])
    .join("path")
    .attr("d", (d) => line(d));
  line = lineD3()
    .defined((d) => !Number.isNaN(d["zero"]))
    .x((d) => xt(d.date))
    .y((d) => yt(d["zero"]));
  _this.svgNodeSpread["lowerLineSpread"]
    .data([filtered])
    .join("path")
    .attr("d", (d) => line(d));
  area = areaD3()
    .defined((d) => !Number.isNaN(d[_this.spreadFieldName]))
    .defined((d) => !Number.isNaN(d["zero"]))
    .x((d) => xt(d.date))
    .y0((d) => yt(d[_this.spreadFieldName]))
    .y1((d) => yt(d["zero"]));
  _this.svgNodeSpread["areaSpread"]
    .data([filtered])
    .join("path")
    .attr("d", area);
  _this.svgNodeSpread["zeroLineSpread"].attr("y1", yt(0)).attr("y2", yt(0));

  // y axis
  let tickCount = _this.svgNodeSpread.height / 40;
  _this.svgNodeSpread.gY
    .transition()
    .duration(500)
    .call(
      axisLeft()
        .tickSize(-_this.svgNodeSpread.width)
        .ticks(tickCount)
        .tickSizeOuter(0)
        .tickPadding(10)
        .ticks(tickCount)
        .scale(_this.svgNodeSpread.y),
    );
}
